import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { useEffect, useState } from 'react';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
  Heading1, Heading2, Heading3, Pilcrow,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Quote, Minus,
  ImageIcon, Link as LinkIcon, Code2,
  Undo2, Redo2, Highlighter, Palette,
  Pencil,
} from 'lucide-react';
import { MediaPickerModal } from '../../ui/MediaPickerModal.tsx';
import { VariablePicker } from './VariablePicker.tsx';

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

// ── Toolbar button ──────────────────────────────────────────────────────────

function Btn({ active, onClick, children, title, disabled }: {
  active?: boolean; onClick: () => void; children: React.ReactNode; title?: string; disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-zinc-900 text-white'
          : disabled
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span className="w-px h-5 bg-gray-200 mx-0.5 shrink-0" />;
}

// ── Full toolbar ────────────────────────────────────────────────────────────

function Toolbar({ editor, onInsertImage }: { editor: any; onInsertImage: () => void }) {
  const s = 15;

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 px-2 py-1.5 bg-slate-850">
      {/* Text formatting */}
      <Btn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
        <Bold size={s} />
      </Btn>
      <Btn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
        <Italic size={s} />
      </Btn>
      <Btn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
        <UnderlineIcon size={s} />
      </Btn>
      <Btn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
        <Strikethrough size={s} />
      </Btn>
      <Btn active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title="Inline Code">
        <Code size={s} />
      </Btn>
      <Btn active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()} title="Highlight">
        <Highlighter size={s} />
      </Btn>

      <Sep />

      {/* Headings */}
      <Btn active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1">
        <Heading1 size={s} />
      </Btn>
      <Btn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
        <Heading2 size={s} />
      </Btn>
      <Btn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">
        <Heading3 size={s} />
      </Btn>
      <Btn active={!editor.isActive('heading')} onClick={() => editor.chain().focus().setParagraph().run()} title="Paragraph">
        <Pilcrow size={s} />
      </Btn>

      <Sep />

      {/* Alignment */}
      <Btn active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align Left">
        <AlignLeft size={s} />
      </Btn>
      <Btn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align Center">
        <AlignCenter size={s} />
      </Btn>
      <Btn active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align Right">
        <AlignRight size={s} />
      </Btn>
      <Btn active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()} title="Justify">
        <AlignJustify size={s} />
      </Btn>

      <Sep />

      {/* Lists & blocks */}
      <Btn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">
        <List size={s} />
      </Btn>
      <Btn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Ordered List">
        <ListOrdered size={s} />
      </Btn>
      <Btn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote">
        <Quote size={s} />
      </Btn>
      <Btn active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code Block">
        <Code2 size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
        <Minus size={s} />
      </Btn>

      <Sep />

      {/* Insert */}
      <Btn onClick={onInsertImage} title="Insert Image">
        <ImageIcon size={s} />
      </Btn>
      <Btn
        active={editor.isActive('link')}
        onClick={() => {
          if (editor.isActive('link')) {
            editor.chain().focus().unsetLink().run();
          } else {
            const url = window.prompt('Link URL:');
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }
        }}
        title="Link"
      >
        <LinkIcon size={s} />
      </Btn>

      <Sep />

      {/* Color */}
      <Btn onClick={() => {
        const color = window.prompt('Text color (hex):', '#');
        if (color && color !== '#') editor.chain().focus().setColor(color).run();
      }} title="Text Color">
        <Palette size={s} />
      </Btn>

      <Sep />

      {/* History */}
      <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        <Undo2 size={s} />
      </Btn>
      <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        <Redo2 size={s} />
      </Btn>

      {/* Variable insert */}
      <VariablePicker
        onInsert={(tag) => editor.chain().focus().insertContent(tag).run()}
        className="shrink-0 flex items-center justify-center w-7 h-7 rounded bg-zinc-800 hover:bg-zinc-800 text-white text-[10px] font-bold transition-colors"
      />
    </div>
  );
}

// ── Modal Editor ────────────────────────────────────────────────────────────

function RichTextModal({ value, onChange, onClose, placeholder }: Props & { onClose: () => void }) {
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [tab, setTab] = useState<'visual' | 'html'>('visual');
  const [htmlSource, setHtmlSource] = useState(value || '');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-zinc-700 underline' } }),
      Placeholder.configure({ placeholder: placeholder ?? 'Start typing…' }),
      Image.configure({ inline: false, allowBase64: false }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: false }),
      TextStyle,
      Color,
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm prose-gray max-w-none min-h-[400px] px-4 py-3 text-sm text-gray-900 focus:outline-none',
      },
    },
  });

  // Sync when switching tabs
  const switchTab = (next: 'visual' | 'html') => {
    if (!editor) return;
    if (next === 'html' && tab === 'visual') {
      setHtmlSource(editor.getHTML());
    } else if (next === 'visual' && tab === 'html') {
      editor.commands.setContent(htmlSource, { emitUpdate: false });
    }
    setTab(next);
  };

  const handleDone = () => {
    if (tab === 'html') {
      onChange(htmlSource);
    } else if (editor) {
      onChange(editor.getHTML());
    }
    onClose();
  };

  if (!editor) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={onClose}>
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col border border-gray-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <span className="text-sm font-semibold text-gray-900">Rich Text Editor</span>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => switchTab('visual')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    tab === 'visual' ? 'bg-zinc-900 text-white' : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Visual
                </button>
                <button
                  onClick={() => switchTab('html')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    tab === 'html' ? 'bg-zinc-900 text-white' : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  HTML
                </button>
              </div>
            </div>
            <button
              onClick={handleDone}
              className="px-4 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium rounded-lg transition-colors"
            >
              Done
            </button>
          </div>

          {/* Visual tab — always mounted, hidden when HTML tab active */}
          <div className={tab === 'visual' ? 'flex flex-col flex-1 min-h-0' : 'hidden'}>
            <Toolbar editor={editor} onInsertImage={() => setMediaPickerOpen(true)} />
            <div className="flex-1 overflow-y-auto">
              <EditorContent editor={editor} />
            </div>
          </div>

          {/* HTML tab — Monaco Editor */}
          <div className={tab === 'html' ? 'flex-1 min-h-0 h-[500px]' : 'hidden'}>
            <MonacoHtmlEditor value={htmlSource} onChange={setHtmlSource} />
          </div>
        </div>
      </div>

      <MediaPickerModal
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        allowedTypes={['image']}
        onSelect={(url) => {
          editor.chain().focus().setImage({ src: url }).run();
          setMediaPickerOpen(false);
        }}
      />
    </>
  );
}

// ── Monaco HTML Editor (lazy loaded) ────────────────────────────────────────

import { lazy, Suspense } from 'react';
const MonacoEditorLazy = lazy(() => import('@monaco-editor/react'));

function MonacoHtmlEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-full text-gray-500 text-sm">Loading editor…</div>}>
      <MonacoEditorLazy
        height="100%"
        language="html"
        theme="vs-dark"
        value={value}
        onChange={(v) => onChange(v ?? '')}
        options={{
          minimap: { enabled: false },
          fontSize: 13,
          wordWrap: 'on',
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          padding: { top: 12, bottom: 12 },
        }}
      />
    </Suspense>
  );
}

// ── Exported Component ──────────────────────────────────────────────────────

export function SimpleRichTextEditor({ value, onChange, placeholder }: Props) {
  const [modalOpen, setModalOpen] = useState(false);

  // Strip HTML for preview
  const previewText = value
    ? value.replace(/<[^>]*>/g, '').slice(0, 120) + (value.length > 120 ? '…' : '')
    : '';

  return (
    <>
      <div
        onClick={() => setModalOpen(true)}
        className="rounded-md border border-gray-300 bg-white px-3 py-2 cursor-pointer hover:border-zinc-400 transition-colors group"
      >
        {previewText ? (
          <p className="text-xs text-gray-700 line-clamp-3">{previewText}</p>
        ) : (
          <p className="text-xs text-gray-500 italic">{placeholder ?? 'Click to edit content…'}</p>
        )}
        <div className="flex items-center gap-1 mt-1.5 text-[10px] text-zinc-700 group-hover:text-zinc-900">
          <Pencil size={10} />
          <span>Edit content</span>
        </div>
      </div>

      {modalOpen && (
        <RichTextModal
          value={value}
          onChange={onChange}
          onClose={() => setModalOpen(false)}
          placeholder={placeholder}
        />
      )}
    </>
  );
}
